/**
 * 顶栏 —— 48px sticky，左面包屑 + 右全局搜索触发器 + 登出（03-UIUX.md §4.3）。
 *
 * 明确不做的事：顶栏**不放页面级操作按钮**。页面操作属于内容区的操作栏；
 * 把「新建」塞进顶栏会让它在所有页面都存在，产生「在错误页面点了对的按钮」的误操作。
 * 登出属于**会话级**操作（全局存在），故放顶栏右侧，与主题切换同一分区（OD-018）。
 */
import { Icon } from '../icons/Icon';
import { ICON_SIZE } from '../tokens';
import { useAuth } from '../auth/AuthContext';

export interface TopbarProps {
  readonly crumbs: readonly string[];
  readonly onOpenMenu: () => void;
  readonly onOpenSearch: () => void;
}

/** macOS 显示 ⌘K，其余显示 Ctrl K —— 显示错的快捷键比不显示更糟 */
function shortcutLabel(): string {
  const platform = globalThis.navigator?.platform ?? '';
  const isApple = /Mac|iPhone|iPad/i.test(platform);
  return isApple ? '⌘ K' : 'Ctrl K';
}

export function Topbar({ crumbs, onOpenMenu, onOpenSearch }: TopbarProps) {
  const { currentUser, logout } = useAuth();

  async function onLogout() {
    try {
      await logout();
    } catch {
      // 服务端登出失败不阻塞本地清态：会话可能已失效，留在当前页会持续收到 401
      globalThis.location.assign('/login');
    }
  }

  return (
    <header className="topbar">
      <button
        type="button"
        className="btn btn--quiet btn--icon topbar__menu-button"
        onClick={onOpenMenu}
        aria-label="打开导航菜单"
        aria-controls="app-sidebar"
      >
        <Icon name="menu" size={ICON_SIZE.control} />
      </button>

      <nav className="topbar__crumbs" aria-label="面包屑">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <span key={crumb} className="cell-inline">
              {index > 0 ? <Icon name="chevron-right" size={ICON_SIZE.badge} /> : null}
              <span
                className={isLast ? 'topbar__crumb-current' : undefined}
                aria-current={isLast ? 'page' : undefined}
              >
                {crumb}
              </span>
            </span>
          );
        })}
      </nav>

      <div className="topbar__spacer" />

      <button type="button" className="topbar__search" onClick={onOpenSearch}>
        <Icon name="search" size={ICON_SIZE.inline} />
        <span>搜索授权、批次、设备</span>
        <kbd>{shortcutLabel()}</kbd>
      </button>

      {currentUser ? (
        <button
          type="button"
          className="btn btn--quiet btn--icon topbar__logout"
          onClick={onLogout}
          aria-label={`登出（${currentUser.email}）`}
          title={`登出（${currentUser.email}）`}
        >
          <Icon name="log-out" size={ICON_SIZE.control} />
        </button>
      ) : null}
    </header>
  );
}
